import React, { useState } from 'react';
import {
  BookOpen,
  CheckCircle,
  XCircle,
  Plus,
  Edit,
  Trash2,
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import PillToggle from '../../components/shared/PillToggle';
import AdminFAQModal from '../../components/admin/AdminFAQModal';
import { useAdminFAQ } from '../../hooks/useAdminFAQ';
import { formatDate } from '../../utils/dateUtils';

const formatCategory = (category) =>
  (category || 'general').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const AdminFAQ = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const {
    faqs,
    isLoading,
    error,
    filters,
    activeStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics: stats,
    toggleStatus,
    isToggling,
    deleteFAQ,
    createFAQ,
    isCreating,
    updateFAQ,
    isUpdating,
  } = useAdminFAQ();

  const openCreate = () => {
    setEditingFAQ(null);
    setShowModal(true);
  };

  const openEdit = (faq) => {
    setEditingFAQ(faq);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingFAQ(null);
  };

  const handleToggleStatus = (faq) => {
    setTogglingId(faq.id);
    toggleStatus(faq.id, {
      onSettled: () => setTogglingId(null),
    });
  };

  const columns = [
    {
      key: 'question',
      label: 'Question',
      sortable: true,
      width: 'w-2/5',
      render: (faq) => (
        <div className="min-w-0 max-w-md">
          <p className="text-sm font-medium text-gray-900 line-clamp-2" title={faq.question}>
            {faq.question || 'N/A'}
          </p>
          <p className="text-xs text-gray-500 line-clamp-1 mt-0.5" title={faq.answer}>
            {faq.answer || ''}
          </p>
        </div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      width: 'w-32',
      render: (faq) => (
        <span className="text-sm text-gray-700">{formatCategory(faq.category)}</span>
      ),
    },
    {
      key: 'order',
      label: 'Order',
      sortable: true,
      width: 'w-20',
      render: (faq) => (
        <span className="text-sm font-medium text-gray-900 tabular-nums">{faq.order ?? 0}</span>
      ),
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      width: 'w-36',
      render: (faq) => (
        <div
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
              faq.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {faq.is_active ? 'Active' : 'Inactive'}
          </span>
          <PillToggle
            checked={Boolean(faq.is_active)}
            onChange={() => handleToggleStatus(faq)}
            loading={togglingId === faq.id && isToggling}
            disabled={togglingId === faq.id && isToggling}
            variant={faq.is_active ? 'success' : 'amber'}
            onLabel="On"
            offLabel="Off"
            ariaLabel={`Toggle FAQ: ${faq.question}`}
          />
        </div>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 'w-28',
      render: (faq) => (
        <span className="text-sm text-gray-600 whitespace-nowrap">
          {formatDate(faq.created_at, 'medium')}
        </span>
      ),
    },
  ];

  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      variant: 'default',
      onClick: openEdit,
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (faq) => {
        if (window.confirm('Are you sure you want to delete this FAQ?')) {
          deleteFAQ(faq.id);
        }
      },
    },
  ];

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading FAQs</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load FAQs. Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <BookOpen className="w-6 h-6" />
                FAQ Management
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage frequently asked questions and help content
              </p>
            </div>
            <Button onClick={openCreate} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Create FAQ
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total FAQs</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total_faqs || 0}</p>
              </div>
              <BookOpen className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stats.active_faqs || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Views</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{stats.total_views || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Helpful Votes</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stats.total_helpful || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SearchSelect
              label="Status"
              value={filters.is_active}
              onChange={(value) => handleFilterChange('is_active', value)}
              options={activeStatusOptions}
            />
          </div>
        </div>

        <div className="bg-white border rounded-lg p-3 sm:p-4 overflow-hidden">
          <DataTable
            data={faqs}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            onRowClick={openEdit}
            searchPlaceholder="Search by question, answer, or category..."
            onSearch={handleSearch}
            emptyMessage="No FAQs found"
            pageSize={10}
          />
        </div>
      </div>

      <AdminFAQModal
        isOpen={showModal}
        onClose={closeModal}
        faq={editingFAQ}
        createFAQ={createFAQ}
        updateFAQ={updateFAQ}
        isCreating={isCreating}
        isUpdating={isUpdating}
        existingFaqs={faqs}
      />
    </div>
  );
};

export default AdminFAQ;
